/**
 * FloatingHScrollbar — a proxy horizontal scrollbar pinned to the bottom of the
 * viewport, mirroring a wide table's scrollLeft so users never have to scroll
 * to the end of the page before they can pan sideways.
 *
 * Pass a ref to the scroll box (or any wrapper around it — the first
 * horizontally scrollable descendant is used). Renders nothing when the target
 * cannot pan or when its own native scrollbar is already on screen.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const findScroller = (root) => {
  if (!root) return null;
  if (root.scrollWidth > root.clientWidth + 2) return root;
  const nodes = root.querySelectorAll("*");
  for (const n of nodes) {
    if (n.scrollWidth > n.clientWidth + 2) {
      const ox = getComputedStyle(n).overflowX;
      if (ox === "auto" || ox === "scroll") return n;
    }
  }
  return null;
};

export const FloatingHScrollbar = ({ targetRef, disabled = false }) => {
  const proxyRef = useRef(null);
  const scrollerRef = useRef(null);
  const syncingRef = useRef(false);
  const [bar, setBar] = useState(null);      // {left, width, scrollWidth}

  useEffect(() => {
    if (disabled) { setBar(null); return; }
    const update = () => {
      const root = targetRef.current;
      if (!root || getComputedStyle(root).display === "none") return setBar(null);
      const el = findScroller(root);
      scrollerRef.current = el;
      if (!el) return setBar(null);
      const r = el.getBoundingClientRect();
      const onScreen = r.top < window.innerHeight - 60 && r.bottom > 140;
      const ownBarHidden = r.bottom > window.innerHeight - 6;
      const next = onScreen && ownBarHidden
        ? { left: Math.round(r.left), width: Math.round(r.width), scrollWidth: Math.round(el.scrollWidth) }
        : null;
      setBar((prev) => {
        if (!prev && !next) return prev;
        if (prev && next && prev.left === next.left && prev.width === next.width
            && prev.scrollWidth === next.scrollWidth) return prev;
        return next;
      });
    };
    update();
    const onEvt = () => requestAnimationFrame(update);
    window.addEventListener("scroll", onEvt, true);
    window.addEventListener("resize", onEvt);
    const ro = new ResizeObserver(onEvt);
    if (targetRef.current) ro.observe(targetRef.current);
    const mo = new MutationObserver(onEvt);
    if (targetRef.current) mo.observe(targetRef.current, { childList: true, subtree: true });
    return () => {
      window.removeEventListener("scroll", onEvt, true);
      window.removeEventListener("resize", onEvt);
      ro.disconnect();
      mo.disconnect();
    };
  }, [targetRef, disabled]);

  // Mirror the table offset whenever the bar (re)mounts or the table scrolls.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!bar || !el) return;
    if (proxyRef.current) proxyRef.current.scrollLeft = el.scrollLeft;
    const onScroll = () => {
      if (syncingRef.current || !proxyRef.current) return;
      syncingRef.current = true;
      proxyRef.current.scrollLeft = el.scrollLeft;
      requestAnimationFrame(() => { syncingRef.current = false; });
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [bar]);

  const onProxyScroll = () => {
    const el = scrollerRef.current;
    if (syncingRef.current || !el || !proxyRef.current) return;
    syncingRef.current = true;
    el.scrollLeft = proxyRef.current.scrollLeft;
    requestAnimationFrame(() => { syncingRef.current = false; });
  };

  if (!bar) return null;
  return createPortal(
    <div
      ref={proxyRef}
      onScroll={onProxyScroll}
      style={{ left: bar.left, width: bar.width }}
      className="hidden md:block fixed bottom-0 z-[150] overflow-x-auto overflow-y-hidden h-[15px] bg-card border border-b-0 border-border rounded-t-md shadow-[0_-2px_6px_rgba(0,0,0,0.08)] hscroll-proxy"
      data-testid="floating-hscrollbar"
    >
      <div style={{ width: `${bar.scrollWidth}px` }} className="h-[1px]" />
    </div>,
    document.body,
  );
};
